import React, { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axios from '../../utils/axiosConfig';
import { CheckIcon, MagnifyingGlassIcon, PencilIcon, ArrowDownTrayIcon } from '@heroicons/react/24/outline';
import Toast from '../../components/Toast';
import { useToast } from '../../hooks/useToast';
import { useAuth } from '../../contexts/AuthContext';
import { useSelectedCourse } from '../../contexts/SelectedCourseContext';
import { formatCursoLabel, formatCursoNombreAnio } from '../../utils/cursoLabel';

interface Alumno {
  id: number;
  numeroAlumno: string;
  nombre: string;
  apellido: string;
  email: string;
}

interface Curso {
  id: number;
  nombre: string;
  codigo: string;
  tipoEvaluacion: 'nota_completa' | 'nota_por_tema';
  cantidadTemas: number;
  turnos: Turno[];
}

// interface NotaConCursoCompleto extends Nota {
//   curso: {
//     id: number;
//     nombre: string;
//     codigo: string;
//     tipoEvaluacion: 'nota_completa' | 'nota_por_tema';
//     cantidadTemas: number;
//   };
// }

interface Turno {
  id: number;
  nombre: string;
  dia: string;
  horaInicio: string;
  horaFin: string;
}

interface Nota {
  id: number;
  alumnoId: number;
  cursoId: number;
  turnoId: number;
  tipoEvaluacion: 'nota_completa' | 'nota_por_tema';
  notaCompleta?: string;
  notasPorTema?: string[];
  estado: 'pendiente' | 'cargada' | 'rectificada';
  observaciones?: string;
  fechaCarga?: string;
  cargadoPor?: {
    id: number;
    nombre: string;
    apellido: string;
    rol: string;
  };
  rectificadoPor?: {
    id: number;
    nombre: string;
    apellido: string;
    rol: string;
  };
  alumno: Alumno;
  curso: {
    id: number;
    nombre: string;
    codigo: string;
    tipoEvaluacion?: 'nota_completa' | 'nota_por_tema';
    cantidadTemas?: number;
  };
  turno: Turno;
}

const RectificarNotasPage: React.FC = () => {
  const { selectedCourse, courses } = useSelectedCourse();
  const selectedCurso = selectedCourse;
  const [selectedFechaExamenId, setSelectedFechaExamenId] = useState<number | null>(null);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [filtroEstado, setFiltroEstado] = useState<'todas' | 'cargada' | 'rectificada'>('cargada');
  const [filtroAyudante, setFiltroAyudante] = useState<number | null>(null);
  const [notasRectificadas, setNotasRectificadas] = useState<{ [key: string]: string | string[] }>({});
  const [observacionesRectificadas, setObservacionesRectificadas] = useState<{ [key: string]: string }>({});
  const [notaSeleccionada, setNotaSeleccionada] = useState<Nota | null>(null);
  const queryClient = useQueryClient();
  const { toast, showSuccess, showError, hideToast } = useToast();
  const { user } = useAuth();

  // Usar cursos del contexto
  const cursos = courses;

  // Interface para fechas de examen
  interface FechaExamen {
    id: number;
    fecha: string;
    tipo: string;
    descripcion: string | null;
    fechaMuestra: string | null;
    activa: boolean;
  }

  // Fetch fechas de examen del curso
  const { data: fechasExamen } = useQuery({
    queryKey: ['fechas-examen', selectedCurso?.id],
    queryFn: async (): Promise<FechaExamen[]> => {
      if (!selectedCurso) return [];
      try {
        const response = await axios.get(`/fechas-examen/curso/${selectedCurso.id}`);
        return Array.isArray(response.data) ? response.data : [];
      } catch (error) {
        console.error('Error fetching fechas de examen:', error);
        return [];
      }
    },
    enabled: !!selectedCurso,
  });

  // Fetch ayudantes y JTP (quienes pueden cargar/corregir notas)
  const { data: docentesCarga } = useQuery({
    queryKey: ['ayudantes-jtp'],
    queryFn: async () => {
      try {
        const response = await axios.get(`/usuarios/ayudantes/list`);
        return Array.isArray(response.data) ? response.data : [];
      } catch (error) {
        console.error('Error fetching docentes:', error);
        return [];
      }
    },
  });

  const rolLabel = (rol: string) => {
    if (rol === 'jtp') return 'JTP';
    if (rol === 'ayudante') return 'Ayudante';
    return rol;
  };

  const renderDocente = (docente?: { nombre: string; apellido: string; rol?: string }) => {
    if (!docente) return <span className="text-sm text-gray-400">—</span>;
    return (
      <div>
        <div className="text-sm text-gray-900">
          {docente.apellido}, {docente.nombre}
        </div>
        {docente.rol && (
          <span className={`inline-flex mt-1 px-1.5 py-0.5 text-xs font-medium rounded ${
            docente.rol === 'jtp' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'
          }`}>
            {rolLabel(docente.rol)}
          </span>
        )}
      </div>
    );
  };

  // Fetch notas por fecha de examen
  const { data: notas, isLoading: loadingNotas, isFetching: fetchingNotas } = useQuery({
    queryKey: ['notas-rectificar', selectedCurso?.id, selectedFechaExamenId],
    queryFn: async () => {
      if (!selectedCurso || !selectedFechaExamenId) return [];
      try {
        const response = await axios.get(`/notas/curso/${selectedCurso.id}/fecha-examen/${selectedFechaExamenId}`);
        console.log('Notas recibidas para rectificar:', response.data?.length || 0);
        return Array.isArray(response.data) ? response.data : [];
      } catch (error) {
        console.error('Error fetching notas:', error);
        return [];
      }
    },
    enabled: !!selectedCurso && !!selectedFechaExamenId,
  });

  // Filtrar y ordenar notas
  const notasFiltradasYOrdenadas = useMemo(() => {
    if (!notas) return [];
    
    // Filtrar por estado
    let notasFiltradas = notas;
    if (filtroEstado !== 'todas') {
      notasFiltradas = notas.filter((nota: Nota) => nota.estado === filtroEstado);
    }
    
    // Filtrar por ayudante
    if (filtroAyudante !== null) {
      notasFiltradas = notasFiltradas.filter((nota: Nota) => 
        nota.cargadoPor?.id === filtroAyudante
      );
    }
    
    // Filtrar por término de búsqueda
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      notasFiltradas = notasFiltradas.filter((nota: Nota) => {
        const nombre = `${nota.alumno.nombre} ${nota.alumno.apellido}`.toLowerCase();
        const numeroAlumno = nota.alumno.numeroAlumno.toLowerCase();
        return nombre.includes(term) || numeroAlumno.includes(term);
      });
    }
    
    // Ordenar alfabéticamente
    return notasFiltradas.sort((a: Nota, b: Nota) => {
      const apellidoA = a.alumno.apellido.toLowerCase();
      const apellidoB = b.alumno.apellido.toLowerCase();
      if (apellidoA !== apellidoB) {
        return apellidoA.localeCompare(apellidoB);
      }
      const nombreA = a.alumno.nombre.toLowerCase();
      const nombreB = b.alumno.nombre.toLowerCase();
      return nombreA.localeCompare(nombreB);
    });
  }, [notas, filtroEstado, filtroAyudante, searchTerm]);

  // Rectificar nota
  const rectificarNotaMutation = useMutation({
    mutationFn: async (data: { notaId: number; notasPorTema?: string[]; notaCompleta?: string; observaciones?: string }) => {
      const response = await axios.post(`/notas/${data.notaId}/rectificar`, {
        notasPorTema: data.notasPorTema,
        notaCompleta: data.notaCompleta,
        observaciones: data.observaciones,
      }, {
        params: { usuarioId: user?.id }
      });
      return response.data;
    },
    onSuccess: (data: any, variables: any) => {
      // Actualizar cache local sin refetch
      queryClient.setQueryData(
        ['notas-rectificar', selectedCurso?.id, selectedFechaExamenId],
        (oldData: any) => {
          if (!oldData) return oldData;
          return oldData.map((nota: Nota) => {
            if (nota.id === variables.notaId) {
              return { ...nota, ...data };
            }
            return nota;
          });
        }
      );
      showSuccess('Nota rectificada', 'La nota se ha rectificado correctamente');
      setNotaSeleccionada(null);
      setNotasRectificadas({});
      setObservacionesRectificadas({});
    },
    onError: (error: any) => {
      const errorMessage = error.response?.data?.message || 'Error al rectificar la nota';
      showError('Error al rectificar', errorMessage);
    }
  });

  // Modificar nota rectificada
  const modificarNotaMutation = useMutation({
    mutationFn: async (data: { notaId: number; notasPorTema?: string[]; notaCompleta?: string; observaciones?: string }) => {
      const response = await axios.patch(`/notas/${data.notaId}/modificar-rectificada`, {
        notasPorTema: data.notasPorTema,
        notaCompleta: data.notaCompleta,
        observaciones: data.observaciones,
      }, {
        params: { usuarioId: user?.id }
      });
      return response.data;
    },
    onSuccess: (data: any, variables: any) => {
      // Actualizar cache local sin refetch
      queryClient.setQueryData(
        ['notas-rectificar', selectedCurso?.id, selectedFechaExamenId],
        (oldData: any) => {
          if (!oldData) return oldData;
          return oldData.map((nota: Nota) => {
            if (nota.id === variables.notaId) {
              return { ...nota, ...data };
            }
            return nota;
          });
        }
      );
      showSuccess('Nota modificada', 'La nota rectificada se ha modificado correctamente');
      setNotaSeleccionada(null);
      setNotasRectificadas({});
      setObservacionesRectificadas({});
    },
    onError: (error: any) => {
      const errorMessage = error.response?.data?.message || 'Error al modificar la nota';
      showError('Error al modificar', errorMessage);
    }
  });

  const handleRectificarNota = (nota: Nota) => {
    setNotaSeleccionada(nota);
    // Cargar valores actuales
    if (nota.tipoEvaluacion === 'nota_por_tema' && nota.notasPorTema) {
      const notasData: { [key: string]: string } = {};
      nota.notasPorTema.forEach((notaTema, index) => {
        notasData[`${nota.id}_tema_${index}`] = notaTema;
      });
      setNotasRectificadas(notasData);
    } else if (nota.notaCompleta) {
      setNotasRectificadas({ [`${nota.id}_completa`]: nota.notaCompleta });
    }
    if (nota.observaciones) {
      setObservacionesRectificadas({ [nota.id.toString()]: nota.observaciones });
    }
  };

  const handleGuardarRectificacion = async () => {
    if (!notaSeleccionada) return;

    const notaCompleta = notasRectificadas[`${notaSeleccionada.id}_completa`] as string;
    const cantidadTemas = notaSeleccionada.curso?.cantidadTemas || selectedCurso?.cantidadTemas || 3;
    const notasPorTema = notaSeleccionada.tipoEvaluacion === 'nota_por_tema'
      ? Array.from({ length: cantidadTemas }, (_, i) => 
          (notasRectificadas[`${notaSeleccionada.id}_tema_${i}`] as string) || ''
        )
      : undefined;
    const observaciones = observacionesRectificadas[notaSeleccionada.id.toString()] || '';

    if (notaSeleccionada.estado === 'rectificada') {
      await modificarNotaMutation.mutateAsync({
        notaId: notaSeleccionada.id,
        notaCompleta,
        notasPorTema,
        observaciones,
      });
    } else {
      await rectificarNotaMutation.mutateAsync({
        notaId: notaSeleccionada.id,
        notaCompleta,
        notasPorTema,
        observaciones,
      });
    }
  };

  const handleNotaChange = (notaId: number, temaIndex: number | null, value: string) => {
    const key = temaIndex !== null ? `${notaId}_tema_${temaIndex}` : `${notaId}_completa`;
    setNotasRectificadas(prev => ({ ...prev, [key]: value }));
  };

  const handleObservacionChange = (notaId: number, value: string) => {
    setObservacionesRectificadas(prev => ({ ...prev, [notaId.toString()]: value }));
  };

  const getNotaColor = (nota: string | number) => {
    if (!nota || nota === '') return 'text-gray-400';
    const notaStr = nota.toString().toUpperCase();
    if (notaStr === 'A') return 'text-green-600 font-bold';
    if (notaStr === 'D') return 'text-red-600 font-bold';
    if (notaStr === 'DU') return 'text-yellow-600 font-bold';
    if (notaStr === 'B') return 'text-gray-500 font-bold';
    const notaNum = parseFloat(notaStr);
    if (isNaN(notaNum)) return 'text-gray-600';
    if (notaNum >= 6) return 'text-green-600 font-bold';
    if (notaNum >= 4) return 'text-yellow-600 font-bold';
    return 'text-red-600 font-bold';
  };

  // Generar PDF
  const handleGenerarPDF = async () => {
    if (!selectedCurso || !selectedFechaExamenId) {
      showError('Error', 'Debe seleccionar un curso y una fecha de examen');
      return;
    }

    // Obtener la fecha de examen seleccionada
    const fechaExamenSeleccionada = fechasExamen?.find(fe => fe.id === selectedFechaExamenId);
    if (!fechaExamenSeleccionada) {
      showError('Error', 'No se encontró la fecha de examen seleccionada');
      return;
    }

    try {
      const fechaStr = fechaExamenSeleccionada.fecha.split('T')[0];
      // Obtener datos para el PDF, enviando fechaExamenId para asegurar que se use la fecha correcta
      const response = await axios.get(`/notas/curso/${selectedCurso.id}/pdf/${fechaStr}`, {
        params: { fechaExamenId: selectedFechaExamenId }
      });
      const data = response.data;

      // Generar PDF usando jsPDF
      const { jsPDF } = await import('jspdf');
      const doc = new jsPDF();

      // Configuración de fuentes
      doc.setFont('helvetica');

      // Título principal - Nombre del curso
      doc.setFontSize(18);
      doc.setFont('helvetica', 'bold');
      doc.text(formatCursoNombreAnio(data.curso), 105, 20, { align: 'center' });

      // Información de la fecha de examen
      let currentY = 35;
      if (data.fechaExamen) {
        // Usar fechaMuestra si existe, sino usar la fecha del examen
        const fechaAMostrar = data.fechaExamen.fechaMuestra || data.fechaExamen.fecha;
        
        // Formatear fecha sin problemas de zona horaria
        let fechaFormateada = '';
        if (fechaAMostrar) {
          // Si es una cadena de fecha (YYYY-MM-DD), parsearla manualmente
          if (typeof fechaAMostrar === 'string') {
            const partes = fechaAMostrar.split('T')[0].split('-');
            if (partes.length === 3) {
              const fecha = new Date(parseInt(partes[0]), parseInt(partes[1]) - 1, parseInt(partes[2]));
              fechaFormateada = fecha.toLocaleDateString('es-AR', {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
              });
            } else {
              fechaFormateada = new Date(fechaAMostrar).toLocaleDateString('es-AR', {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
              });
            }
          } else {
            // Si es un objeto Date
            fechaFormateada = new Date(fechaAMostrar).toLocaleDateString('es-AR', {
              year: 'numeric',
              month: 'long',
              day: 'numeric'
            });
          }
        }
        
        doc.setFontSize(14);
        doc.setFont('helvetica', 'bold');
        doc.text(`Fecha de Muestra: ${fechaFormateada}`, 105, currentY, { align: 'center' });
        currentY += 10;

        if (data.fechaExamen.descripcion) {
          doc.setFontSize(12);
          doc.setFont('helvetica', 'normal');
          const descripcionLines = doc.splitTextToSize(data.fechaExamen.descripcion, 170);
          doc.text(descripcionLines, 105, currentY, { align: 'center' });
          currentY += descripcionLines.length * 6 + 5;
        }
      } else {
        doc.setFontSize(14);
        doc.setFont('helvetica', 'bold');
        doc.text(`Fecha: ${new Date(fechaExamenSeleccionada.fecha).toLocaleDateString('es-AR', {
          year: 'numeric',
          month: 'long',
          day: 'numeric'
        })}`, 105, currentY, { align: 'center' });
        currentY += 10;
      }

      // Preparar datos de la tabla
      const startY = currentY + 5;
      let tableY = startY;

      // Encabezados de la tabla
      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      
      const cantidadTemas = data.curso.cantidadTemas || 0;
      if (data.curso.tipoEvaluacion === 'nota_por_tema' && cantidadTemas > 0) {
        // Tabla con notas por tema (dinámico según cantidadTemas del curso)
        const temaHeaders = Array.from({ length: cantidadTemas }, (_, i) => `T${i + 1}`);
        const headers = ['#', 'Legajo', 'Apellido', 'Nombre', ...temaHeaders];
        const colWidths = [10, 30, 45, 45, ...Array(cantidadTemas).fill(20)];
        let x = 10;

        // Dibujar encabezados
        headers.forEach((header, index) => {
          doc.rect(x, tableY, colWidths[index], 8);
          doc.text(header, x + colWidths[index] / 2, tableY + 5, { align: 'center' });
          x += colWidths[index];
        });

        tableY += 8;

        // Datos de alumnos
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(9);
        data.notas.forEach((nota: any, index: number) => {
          if (tableY > 280) {
            doc.addPage();
            tableY = 20;
          }

          x = 10;
          const notasTema = Array.from({ length: cantidadTemas }, (_, i) => nota.notasPorTema?.[i] || '-');
          const rowData = [
            (index + 1).toString(),
            nota.alumno.numeroAlumno,
            nota.alumno.apellido,
            nota.alumno.nombre,
            ...notasTema,
          ];

          rowData.forEach((cell, cellIndex) => {
            doc.rect(x, tableY, colWidths[cellIndex], 7);
            const cellText = cell.toString();
            // Truncar texto si es muy largo
            const maxWidth = colWidths[cellIndex] - 2;
            const truncatedText = doc.splitTextToSize(cellText, maxWidth);
            doc.text(truncatedText, x + colWidths[cellIndex] / 2, tableY + 4, { align: 'center', maxWidth: maxWidth });
            x += colWidths[cellIndex];
          });

          tableY += 7;
        });
      } else {
        // Tabla simple con nota completa
        const headers = ['#', 'Legajo', 'Apellido', 'Nombre', 'Nota Final'];
        const colWidths = [10, 30, 50, 50, 30];
        let x = 10;

        // Dibujar encabezados
        headers.forEach((header, index) => {
          doc.rect(x, tableY, colWidths[index], 8);
          doc.text(header, x + colWidths[index] / 2, tableY + 5, { align: 'center' });
          x += colWidths[index];
        });

        tableY += 8;

        // Datos de alumnos
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(9);
        data.notas.forEach((nota: any, index: number) => {
          if (tableY > 280) {
            doc.addPage();
            tableY = 20;
          }

          x = 10;
          const rowData = [
            (index + 1).toString(),
            nota.alumno.numeroAlumno,
            nota.alumno.apellido,
            nota.alumno.nombre,
            nota.notaCompleta || '-',
          ];

          rowData.forEach((cell, cellIndex) => {
            doc.rect(x, tableY, colWidths[cellIndex], 7);
            const cellText = cell.toString();
            const maxWidth = colWidths[cellIndex] - 2;
            const truncatedText = doc.splitTextToSize(cellText, maxWidth);
            doc.text(truncatedText, x + colWidths[cellIndex] / 2, tableY + 4, { align: 'center', maxWidth: maxWidth });
            x += colWidths[cellIndex];
          });

          tableY += 7;
        });
      }

      // Pie de página
      doc.setFontSize(8);
      doc.setFont('helvetica', 'normal');
      doc.text(`Generado el: ${new Date().toLocaleString('es-AR')}`, 10, 290);
      doc.text(`Total de alumnos: ${data.totalAlumnos}`, 105, 290, { align: 'center' });

      // Guardar PDF
      const fileName = `Listado_Notas_${formatCursoNombreAnio(data.curso).replace(/\s+/g, '_')}_${fechaStr}.pdf`;
      doc.save(fileName);

      showSuccess('PDF generado', 'El archivo se ha descargado correctamente');
    } catch (error: any) {
      console.error('Error al generar PDF:', error);
      showError('Error', error.response?.data?.message || 'No se pudo generar el PDF');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-gray-900">Rectificar Notas</h2>
        <div className="text-sm text-gray-600">
          Validar y rectificar notas cargadas por ayudantes
        </div>
      </div>

      {/* Selector de fecha */}
      {selectedCurso && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-sm text-blue-800">
              <span className="font-medium">Curso seleccionado:</span> {formatCursoLabel(selectedCurso)}
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-1 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Fecha de Examen</label>
              <select
              value={selectedFechaExamenId || ''}
              onChange={(e) => {
                const id = e.target.value ? parseInt(e.target.value, 10) : null;
                setSelectedFechaExamenId(id);
                setSearchTerm('');
              }}
              disabled={!selectedCurso}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100"
            >
              <option value="">Seleccionar fecha de examen</option>
              {Array.isArray(fechasExamen) && fechasExamen.map((fechaExamen: FechaExamen) => {
                const fechaStr = fechaExamen.fecha.split('T')[0];
                const tipoStr = fechaExamen.tipo === 'primera_fecha' ? 'Primera Fecha' :
                               fechaExamen.tipo === 'recuperatorio_1' ? 'Recuperatorio 1' :
                               fechaExamen.tipo === 'recuperatorio_2' ? 'Recuperatorio 2' :
                               fechaExamen.tipo === 'fecha_extra' ? 'Fecha Extra' : fechaExamen.tipo;
                const label = fechaExamen.descripcion 
                  ? `${fechaStr} - ${tipoStr} (${fechaExamen.descripcion})`
                  : `${fechaStr} - ${tipoStr}`;
                return (
                  <option key={fechaExamen.id} value={fechaExamen.id}>
                    {label}
                  </option>
                );
              })}
            </select>
            </div>
          </div>
        </div>
      )}

      {/* Filtros y búsqueda */}
      {selectedCurso && selectedFechaExamenId && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
            <div className="flex flex-wrap gap-4 items-center">
              <div className="flex gap-2 items-center">
                <label className="text-sm font-medium text-gray-700">Estado:</label>
                <select
                  value={filtroEstado}
                  onChange={(e) => setFiltroEstado(e.target.value as 'todas' | 'cargada' | 'rectificada')}
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="todas">Todas</option>
                  <option value="cargada">Pendientes de rectificación</option>
                  <option value="rectificada">Rectificadas</option>
                </select>
              </div>
              <div className="flex gap-2 items-center">
                <label className="text-sm font-medium text-gray-700">Cargado por:</label>
                <select
                  value={filtroAyudante || ''}
                  onChange={(e) => setFiltroAyudante(e.target.value ? parseInt(e.target.value) : null)}
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">Todos</option>
                  {Array.isArray(docentesCarga) && docentesCarga.map((docente: any) => (
                    <option key={docente.id} value={docente.id}>
                      {docente.apellido}, {docente.nombre} ({rolLabel(docente.rol)})
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="relative w-full md:w-64">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Buscar alumno..."
                className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              />
            </div>
          </div>
        </div>
      )}

      {/* Lista de notas */}
      {selectedCurso && selectedFechaExamenId && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200 bg-gray-50 flex justify-between items-center">
            <div>
              <h3 className="text-lg font-medium text-gray-900">
                Notas - {formatCursoNombreAnio(selectedCurso)} {(() => {
                  const fechaExamen = fechasExamen?.find(fe => fe.id === selectedFechaExamenId);
                  if (fechaExamen) {
                    const fechaStr = fechaExamen.fecha.split('T')[0];
                    return `(${fechaStr})`;
                  }
                  return '';
                })()}
              </h3>
              {(searchTerm || filtroAyudante) && (
                <p className="text-sm text-gray-600 mt-1">
                  Mostrando {notasFiltradasYOrdenadas.length} de {notas?.length || 0} notas
                </p>
              )}
            </div>
            <button
              onClick={handleGenerarPDF}
              disabled={!notas || notas.length === 0}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ArrowDownTrayIcon className="h-5 w-5 mr-2" />
              Generar PDF
            </button>
          </div>
          
          {(loadingNotas || fetchingNotas) ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
                <p className="mt-4 text-gray-600">Cargando notas...</p>
              </div>
            </div>
          ) : (

          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Alumno
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Nota Original
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Estado
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Cargado por
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Rectificado por
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Acciones
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {notasFiltradasYOrdenadas.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-6 py-8 text-center text-gray-500">
                      {searchTerm ? 'No se encontraron notas con ese criterio' : 'No hay notas para esta fecha'}
                    </td>
                  </tr>
                ) : (
                  notasFiltradasYOrdenadas.map((nota: Nota) => (
                    <tr key={nota.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {nota.alumno.nombre} {nota.alumno.apellido}
                        </div>
                        <div className="text-sm text-gray-500">
                          N° {nota.alumno.numeroAlumno}
                        </div>
                        <div className="text-xs text-blue-600">
                          {nota.turno.nombre}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        {nota.tipoEvaluacion === 'nota_completa' ? (
                          <span className={getNotaColor(nota.notaCompleta || '')}>
                            {nota.notaCompleta || 'N/A'}
                          </span>
                        ) : (
                          <div className="flex space-x-1 justify-center">
                            {Array.isArray(nota.notasPorTema) && nota.notasPorTema.map((notaTema, index) => (
                              <span key={index} className={`px-2 py-1 bg-gray-100 rounded text-xs ${getNotaColor(notaTema)}`}>
                                T{index + 1}: {notaTema || 'N/A'}
                              </span>
                            ))}
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          nota.estado === 'cargada' 
                            ? 'bg-green-100 text-green-800' 
                            : nota.estado === 'rectificada'
                            ? 'bg-blue-100 text-blue-800'
                            : 'bg-yellow-100 text-yellow-800'
                        }`}>
                          {nota.estado === 'cargada' ? 'Cargada' : 
                           nota.estado === 'rectificada' ? 'Rectificada' : 'Pendiente'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        {renderDocente(nota.cargadoPor)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        {nota.estado === 'rectificada'
                          ? renderDocente(nota.rectificadoPor)
                          : <span className="text-sm text-gray-400">—</span>}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        <button
                          onClick={() => handleRectificarNota(nota)}
                          className="inline-flex items-center px-3 py-1 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                        >
                          <PencilIcon className="h-4 w-4 mr-1" />
                          {nota.estado === 'rectificada' ? 'Modificar' : 'Rectificar'}
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          )}
        </div>
      )}

      {/* Modal de rectificación */}
      {notaSeleccionada && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                {notaSeleccionada.estado === 'rectificada' ? 'Modificar Nota Rectificada' : 'Rectificar Nota'}
              </h3>
              <div className="mb-4">
                <p className="text-sm text-gray-600">
                  <strong>Alumno:</strong> {notaSeleccionada.alumno.nombre} {notaSeleccionada.alumno.apellido} (N° {notaSeleccionada.alumno.numeroAlumno})
                </p>
                <p className="text-sm text-gray-600">
                  <strong>Curso:</strong> {formatCursoNombreAnio(notaSeleccionada.curso)}
                </p>
                {notaSeleccionada.cargadoPor && (
                  <p className="text-sm text-gray-600">
                    <strong>Cargado por:</strong>{' '}
                    {notaSeleccionada.cargadoPor.apellido}, {notaSeleccionada.cargadoPor.nombre}
                    {notaSeleccionada.cargadoPor.rol && (
                      <span className="ml-2 text-xs font-medium text-blue-700">
                        ({rolLabel(notaSeleccionada.cargadoPor.rol)})
                      </span>
                    )}
                  </p>
                )}
                {notaSeleccionada.rectificadoPor && (
                  <p className="text-sm text-gray-600">
                    <strong>Rectificado por:</strong>{' '}
                    {notaSeleccionada.rectificadoPor.apellido}, {notaSeleccionada.rectificadoPor.nombre}
                    {notaSeleccionada.rectificadoPor.rol && (
                      <span className="ml-2 text-xs font-medium text-purple-700">
                        ({rolLabel(notaSeleccionada.rectificadoPor.rol)})
                      </span>
                    )}
                  </p>
                )}
              </div>

              <div className="space-y-4">
                {notaSeleccionada.tipoEvaluacion === 'nota_por_tema' ? (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Notas por Tema</label>
                    <div className="grid grid-cols-3 gap-4">
                      {Array.from({ length: notaSeleccionada.curso?.cantidadTemas || selectedCurso?.cantidadTemas || 3 }, (_, i) => {
                        const key = `${notaSeleccionada.id}_tema_${i}`;
                        const valor = notasRectificadas[key] as string || notaSeleccionada.notasPorTema?.[i] || '';
                        const opcionesNota = [
                          { value: '', label: 'N/A' },
                          { value: 'A', label: 'A - Aprobado' },
                          { value: 'D', label: 'D - Desaprobado' },
                          { value: 'DU', label: 'DU - Desaprobado por ausencia' },
                          { value: 'B', label: 'B - Blanco' },
                        ];
                        return (
                          <div key={i}>
                            <label className="block text-xs text-gray-600 mb-1">Tema {i + 1}</label>
                            <select
                              value={valor}
                              onChange={(e) => handleNotaChange(notaSeleccionada.id, i, e.target.value)}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            >
                              {opcionesNota.map((opcion) => (
                                <option key={opcion.value} value={opcion.value}>
                                  {opcion.value || opcion.label}
                                </option>
                              ))}
                            </select>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ) : (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Nota Completa</label>
                    <select
                      value={notasRectificadas[`${notaSeleccionada.id}_completa`] as string || notaSeleccionada.notaCompleta || ''}
                      onChange={(e) => handleNotaChange(notaSeleccionada.id, null, e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      {[
                        { value: '', label: 'N/A' },
                        { value: 'A', label: 'A - Aprobado' },
                        { value: 'D', label: 'D - Desaprobado' },
                        { value: 'DU', label: 'DU - Desaprobado por ausencia' },
                        { value: 'B', label: 'B - Blanco' },
                      ].map((opcion) => (
                        <option key={opcion.value} value={opcion.value}>
                          {opcion.value || opcion.label}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Observaciones</label>
                  <textarea
                    value={observacionesRectificadas[notaSeleccionada.id.toString()] || notaSeleccionada.observaciones || ''}
                    onChange={(e) => handleObservacionChange(notaSeleccionada.id, e.target.value)}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Observaciones..."
                  />
                </div>
              </div>

              <div className="flex justify-end gap-3 mt-6">
                <button
                  onClick={() => {
                    setNotaSeleccionada(null);
                    setNotasRectificadas({});
                    setObservacionesRectificadas({});
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleGuardarRectificacion}
                  disabled={rectificarNotaMutation.isPending || modificarNotaMutation.isPending}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                >
                  <CheckIcon className="h-4 w-4 mr-1" />
                  {rectificarNotaMutation.isPending || modificarNotaMutation.isPending 
                    ? 'Guardando...' 
                    : notaSeleccionada.estado === 'rectificada' 
                      ? 'Modificar' 
                      : 'Rectificar'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <Toast
        type={toast.type}
        title={toast.title}
        message={toast.message}
        isVisible={toast.isVisible}
        onClose={hideToast}
      />
    </div>
  );
};

export default RectificarNotasPage;

